import type { TaskView, WorkflowView } from '@/lib/api/generated/model';

/**
 * Whose subtask a task is, said the way a row can print it: "Release 1.4 ›
 * Design reviews" (spec 150 FR-005).
 *
 * `stepLabel` names the step of the parent that *created* this subtask, not the
 * step the parent stands on now: once a release has moved from its design
 * reviews to its QA checks, its design reviews are still its design reviews.
 */
export interface SubtaskParentRef {
  taskId: string;
  title: string;
  workflowKey: string;
  /** `null` when neither the creating step nor the batch's workflow is known. */
  stepLabel: string | null;
  /**
   * False when the parent cannot be shown or opened: it is hidden, its
   * workflow has no board, or it is not in the workspace's tasks at all. The
   * row then says only "Subtask".
   */
  reachable: boolean;
}

/** What the resolver looks things up in, built once for a whole workspace. */
export interface SubtaskParentLookup {
  tasksById: ReadonlyMap<string, TaskView>;
  /** The task each execution belongs to: the parent of an older subtask. */
  taskByExecutionId: ReadonlyMap<string, TaskView>;
  workflowsById: ReadonlyMap<string, WorkflowView>;
  /** Step labels per workflow, as the board names them. */
  stepLabels: ReadonlyMap<string, ReadonlyMap<string, string>>;
}

/**
 * Index the workspace once, so resolving a parent is a few map reads.
 *
 * `tasks` should be every task the page may name, not just those a search left
 * on screen: filtering for "Search results" must not hide which release it
 * belongs to.
 */
export function buildSubtaskParentLookup(
  workflows: readonly WorkflowView[],
  tasks: readonly TaskView[],
): SubtaskParentLookup {
  const tasksById = new Map<string, TaskView>();
  const taskByExecutionId = new Map<string, TaskView>();
  for (const task of tasks) {
    tasksById.set(task.id, task);
    for (const run of task.executions ?? [])
      taskByExecutionId.set(run.id, task);
  }

  const workflowsById = new Map<string, WorkflowView>();
  const stepLabels = new Map<string, Map<string, string>>();
  for (const workflow of workflows) {
    workflowsById.set(workflow.id, workflow);
    // The board labels a step by its name (`boardModel.ts`), so the
    // definition is read directly: a workflow whose board will not build still
    // names the steps that created its subtasks.
    const labels = new Map<string, string>([
      [workflow.start_step.id, workflow.start_step.name],
      [workflow.done_step.id, workflow.done_step.name],
    ]);
    for (const phase of workflow.phases) {
      for (const step of phase.steps ?? []) labels.set(step.id, step.name);
    }
    stepLabels.set(workflow.id, labels);
  }

  return { tasksById, taskByExecutionId, workflowsById, stepLabels };
}

/**
 * The parent of a subtask, or `null` for a task that is not one.
 *
 * The parent is `parent_task_id` (service spec 119). A subtask created before
 * that has only the run that created it, so the task owning
 * `created_by_execution_id` is the fallback — the same one the run page uses
 * (`linked-runs/runBoard.ts`).
 */
export function resolveSubtaskParent(
  task: TaskView,
  lookup: SubtaskParentLookup,
): SubtaskParentRef | null {
  if (!task.is_subtask) return null;

  const creatingRunId = task.created_by_execution_id ?? null;
  const parent =
    (task.parent_task_id
      ? lookup.tasksById.get(task.parent_task_id)
      : undefined) ??
    (creatingRunId ? lookup.taskByExecutionId.get(creatingRunId) : undefined);

  if (!parent) {
    return {
      taskId: task.parent_task_id ?? '',
      title: '',
      workflowKey: '',
      stepLabel: null,
      reachable: false,
    };
  }

  const workflow = lookup.workflowsById.get(parent.workflow_id);
  const creatingRun = creatingRunId
    ? (parent.executions ?? []).find((run) => run.id === creatingRunId)
    : undefined;
  const stepLabel =
    (creatingRun
      ? lookup.stepLabels
          .get(parent.workflow_id)
          ?.get(creatingRun.step_id_snapshot)
      : undefined) ??
    parent.linked_tasks?.workflow_name ??
    null;

  return {
    taskId: parent.id,
    title: parent.title,
    workflowKey: workflow?.key ?? '',
    stepLabel,
    reachable:
      parent.hidden_at == null &&
      workflow !== undefined &&
      workflow.status === 'published',
  };
}
